import {
  coverPatchToWire,
  mapBrandKitDto,
  mapCreateLogoIntentResultDto,
  type BrandKit,
  type BrandKitDto,
  type CreateLogoIntentResultDto,
  type UploadedListDto,
  type LogoSlot,
} from '@/types';

import type {
  BrandKitCreateRequest,
  BrandKitExtractResult,
  BrandKitsService,
  BrandKitUpdateRequest,
  CreateLogoIntentRequest,
} from '../brandKitsService';
import { httpRequestWithRefresh, type HttpClientConfig } from './httpApiClient';
import { buildCoverFormData, COVER_PATH_SUFFIX } from './httpCoverUpload';
import { unwrapResult } from './httpEnvelope';

/**
 * HTTP impl of :type:`BrandKitsService`
 * -----------------------------------------------------------------------------
 * 對齊 ``docs/BRAND_KITS.md`` 7 endpoints；全部走 ``httpRequestWithRefresh``
 * （cookie + CSRF + 401 refresh chain 全自動繼承），跟 R14 ``httpUploadsService``
 * 同一條基礎設施。
 *
 * **camelCase ↔ snake_case body mapping** 在這層處理；UI 端永遠看不到 snake_case。
 */

const PATH = {
  list: '/api/brand_kits',
  create: '/api/brand_kits',
  item: (id: string) => `/api/brand_kits/${encodeURIComponent(id)}`,
  duplicate: (id: string) => `/api/brand_kits/${encodeURIComponent(id)}/duplicate`,
  logoIntent: (id: string) => `/api/brand_kits/${encodeURIComponent(id)}/logo/intent`,
  logoFinalize: (id: string) => `/api/brand_kits/${encodeURIComponent(id)}/logo/finalize`,
  // BK-1 PDF 抽取(FB-20260827-0002;multipart,同 cover 的 FormData 慣例)
  extract: '/api/brand_kits/extract',
  // R245 封面 — ⚠️ 與上面兩支 logo 端點無關(那是品牌資產,這是卡片門面圖)。
  cover: (id: string) => `/api/brand_kits/${encodeURIComponent(id)}${COVER_PATH_SUFFIX}`,
} as const;

/** Build snake_case body for create endpoint，省去 undefined 欄位。 */
function buildCreateBody(req: BrandKitCreateRequest): Record<string, unknown> {
  const body: Record<string, unknown> = {
    name: req.name,
    colors: {
      // ⚠️ 這幾欄是 **CSV 多值字串**(BE 2026-08-17),不是單一色碼 —— 組裝由
      // `colorsCsv` 那組工具負責,這裡照原樣送。
      primary: req.colors.primary,
      secondary: req.colors.secondary,
      accent: req.colors.accent,
      text: req.colors.text,
      bg: req.colors.bg,
      palette: req.colors.palette,
    },
  };
  if (req.toneOfVoice !== undefined) body.tone_of_voice = req.toneOfVoice;
  if (req.styleKeywords !== undefined) body.style_keywords = req.styleKeywords;
  if (req.fonts !== undefined) body.fonts = req.fonts; // heading / body 已對應
  if (req.prohibited !== undefined) body.prohibited = req.prohibited;
  // R43 ABCD 4 欄 — undefined skip(BE Pydantic `default_factory=list` 補 [])。
  if (req.brandVariations !== undefined) body.brand_variations = req.brandVariations;
  if (req.brandedProducts !== undefined) body.branded_products = req.brandedProducts;
  if (req.brandedProductsCategories !== undefined) {
    body.branded_products_categories = req.brandedProductsCategories;
  }
  if (req.brandedCallToActions !== undefined) {
    body.branded_call_to_actions = req.brandedCallToActions;
  }
  // R245 — `none` 時不夾帶 `color: null`(BE extra="forbid")。
  if (req.cover !== undefined) body.cover = coverPatchToWire(req.cover);
  if (req.intro !== undefined) body.intro = req.intro;
  if (req.audience !== undefined) body.audience = req.audience;
  if (req.allowSkipWarning !== undefined) body.allow_skip_warning = req.allowSkipWarning;
  return body;
}

function buildUpdateBody(patch: BrandKitUpdateRequest): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (patch.name !== undefined) body.name = patch.name;
  if (patch.toneOfVoice !== undefined) body.tone_of_voice = patch.toneOfVoice; // null 也保留（後端：null 視同不改）
  if (patch.styleKeywords !== undefined) body.style_keywords = patch.styleKeywords;
  if (patch.colors !== undefined) {
    body.colors = {
      primary: patch.colors.primary,
      secondary: patch.colors.secondary,
      accent: patch.colors.accent,
      text: patch.colors.text,
      bg: patch.colors.bg,
      palette: patch.colors.palette,
    };
  }
  if (patch.fonts !== undefined) body.fonts = patch.fonts;
  if (patch.prohibited !== undefined) body.prohibited = patch.prohibited;
  // R43 ABCD 4 欄 — Bryan 拍板「覆蓋」語意:edit form 永遠送 current value(form load BE 既有值 →
  // user 沒動就回送 BE 值 = 等於不變;`[]` = user 主動清空)。
  if (patch.brandVariations !== undefined) body.brand_variations = patch.brandVariations;
  if (patch.brandedProducts !== undefined) body.branded_products = patch.brandedProducts;
  if (patch.brandedProductsCategories !== undefined) {
    body.branded_products_categories = patch.brandedProductsCategories;
  }
  if (patch.brandedCallToActions !== undefined) {
    body.branded_call_to_actions = patch.brandedCallToActions;
  }
  if (patch.cover !== undefined) body.cover = coverPatchToWire(patch.cover);
  if (patch.intro !== undefined) body.intro = patch.intro;
  if (patch.audience !== undefined) body.audience = patch.audience;
  // ⚠️ PATCH 的 `allow_skip_warning` 是 `bool | null`:**不傳 = 不改**(不會被重設)
  if (patch.allowSkipWarning !== undefined) body.allow_skip_warning = patch.allowSkipWarning;
  return body;
}

export function createHttpBrandKitsService(config: HttpClientConfig): BrandKitsService {
  return {
    async create(req: BrandKitCreateRequest): Promise<BrandKit> {
      const env = await httpRequestWithRefresh<BrandKitDto>(
        config,
        'POST',
        PATH.create,
        buildCreateBody(req),
      );
      return mapBrandKitDto(unwrapResult(env, 'brandKits.create'));
    },

    async list(): Promise<BrandKit[]> {
      const env = await httpRequestWithRefresh<UploadedListDto<BrandKitDto>>(
        config,
        'GET',
        PATH.list,
      );
      const items = env.result?.items ?? [];
      return items.map(mapBrandKitDto);
    },

    async get(brandKitId: string): Promise<BrandKit> {
      const env = await httpRequestWithRefresh<BrandKitDto>(
        config,
        'GET',
        PATH.item(brandKitId),
      );
      return mapBrandKitDto(unwrapResult(env, 'brandKits.get'));
    },

    async update(brandKitId: string, patch: BrandKitUpdateRequest): Promise<BrandKit> {
      const env = await httpRequestWithRefresh<BrandKitDto>(
        config,
        'PATCH',
        PATH.item(brandKitId),
        buildUpdateBody(patch),
      );
      return mapBrandKitDto(unwrapResult(env, 'brandKits.update'));
    },

    async remove(brandKitId: string): Promise<BrandKit> {
      const env = await httpRequestWithRefresh<BrandKitDto>(
        config,
        'DELETE',
        PATH.item(brandKitId),
      );
      return mapBrandKitDto(unwrapResult(env, 'brandKits.remove'));
    },

    async duplicate(brandKitId: string): Promise<BrandKit> {
      // T4(docs/50)— body 無;CSRF 由 httpRequestWithRefresh 自動帶(寫操作)。
      const env = await httpRequestWithRefresh<BrandKitDto>(
        config,
        'POST',
        PATH.duplicate(brandKitId),
      );
      return mapBrandKitDto(unwrapResult(env, 'brandKits.duplicate'));
    },

    async createLogoIntent(brandKitId: string, req: CreateLogoIntentRequest) {
      const env = await httpRequestWithRefresh<CreateLogoIntentResultDto>(
        config,
        'POST',
        PATH.logoIntent(brandKitId),
        {
          mime_type: req.mimeType,
          size_bytes: req.sizeBytes,
          slot: req.slot,
        },
      );
      return mapCreateLogoIntentResultDto(unwrapResult(env, 'brandKits.createLogoIntent'));
    },

    async finalizeLogo(brandKitId: string, uploadId: string, slot: LogoSlot) {
      const env = await httpRequestWithRefresh<BrandKitDto>(
        config,
        'POST',
        PATH.logoFinalize(brandKitId),
        { upload_id: uploadId, slot },
      );
      return mapBrandKitDto(unwrapResult(env, 'brandKits.finalizeLogo'));
    },

    async setCoverImage(brandKitId: string, file: File): Promise<BrandKit> {
      // ⚠️ 不是 logo:一次 multipart 就完成,沒有 intent / finalize / GCS PUT。
      const env = await httpRequestWithRefresh<BrandKitDto>(
        config,
        'POST',
        PATH.cover(brandKitId),
        buildCoverFormData(file),
      );
      return mapBrandKitDto(unwrapResult(env, 'brandKits.setCoverImage'));
    },

    async extractFromPdf(file: File): Promise<BrandKitExtractResult> {
      // multipart 一發完成(同 cover;`httpRequest` 對 FormData 不設 Content-Type)。
      // ⚠️ BE 無 idempotency key(multipart)—— 這裡與呼叫端都**不得**自動重試,
      // 重試 = 重扣點(BE router 註明;rate 5/min 是唯一護欄)。
      const fd = new FormData();
      fd.append('file', file);
      const env = await httpRequestWithRefresh<{
        fields?: Record<string, unknown>;
        charged_credits?: number;
      }>(config, 'POST', PATH.extract, fd);
      const dto = unwrapResult(env, 'brandKits.extractFromPdf');
      return {
        fields: dto.fields ?? {},
        chargedCredits: typeof dto.charged_credits === 'number' ? dto.charged_credits : 0,
      };
    },
  };
}
